# Route: /admin/invitations
# Invitation management: send, view, revoke

page-admin-invitation-list:
  type: Page
  archetype: list
  meta:
    title: $t.admin.invitations.meta_title
    auth: required
  data:
    invitations:
      model: InvitationResponse[]
      # /api/invitations is gated by .WithRequiresFilter("status", "email").
      # `filter:` seeds _queryState so the mount-time fetch carries the param;
      # operator can change the filter via the table header to widen the list.
      filter:
        status: Pending
      refresh: on_mount
    form:
      model: CreateInvitationForm
      initial:
        email: ""
        firstName: ""
        lastName: ""
        intendedRole: ""
    # Options for the invitation role picker: the roles that can be granted
    # (GET /api/users/roles?assignable=true), the same list as the user-detail
    # assignment picker. CreateInvitationHandler refuses a template as the
    # intended role, so the picker never offers one. RoleDto.id mirrors name,
    # so option value = role name.
    roles:
      model: AssignableRole[]
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - invitation-list-header
    - invitation-system-notice
    - invitation-send-form
    - invitation-list-table

# ── Header ───────────────────────────────────────────────────────────────────

invitation-list-header:
  type: PageHeader
  title: $t.admin.invitations.title

# ── Platform-administrator notice ────────────────────────────────────────────
# A SystemAdmin acts in the SYSTEM tenant, whose role catalog holds SystemAdmin
# alone, so the role picker below offers nothing else and every invitation sent
# from here creates a platform administrator (CreateInvitationHandler refuses
# SystemAdmin outside SYSTEM; the ?tenantId= cross-tenant path is TenantAdmin-only).
# That is correct on the server and invisible on the page, so the page says it,
# and points tenant onboarding at the tenant's own Manage Admins page.
#
# Gated on `System:admin`, the sentinel the SPA's permission set carries ONLY when
# the server's system-admin gate elevates the request — an active SystemAdmin
# assignment, the system_admin realm role and a SYSTEM-tenant token, all three.
# UserEffectivePermissionsQuery removes `System` from wildcard expansion, so a
# tenant Admin holding `*:*` never sees it. It is the same gate nav-section-platform uses. The page itself stays
# reachable on Invitation:read, because a SystemAdmin onboarding another platform
# administrator is a real act (Tier-3 scenarios 235/313); the notice informs it
# rather than hiding it.

invitation-system-notice:
  type: Section
  permission: "System:admin"
  a11y:
    role: region
    label: $t.admin.invitations.system_notice_heading
  spatial:
    display: flex
    direction: column
    gap: $spacing.xs
    padding: $spacing.md
    border_inline_start_accent: caution
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    border: $border.default
  children:
    - invitation-system-notice-heading
    - invitation-system-notice-body

invitation-system-notice-heading:
  type: Text
  content: $t.admin.invitations.system_notice_heading
  a11y:
    role: heading
  tokens:
    font_size: $font.size.md
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

invitation-system-notice-body:
  type: Text
  content: $t.admin.invitations.system_notice_body
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

# ── Send invitation form ─────────────────────────────────────────────────────
# Titled-section card pattern (my-tenant-profile.cir.yaml): a heading Text
# states what the form does before the operator reads any field, instead of
# the join semantics living only in one field's small helper caption. F78c
# Act 1.6 — the M1 manual test pass found the un-headed inline field row
# ambiguous.

invitation-send-form:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - invitation-send-form-title
    - invitation-send-form-hint
    - invitation-send-fields

invitation-send-form-title:
  type: Text
  content: $t.admin.invitations.send_form_heading
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.md
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

invitation-send-form-hint:
  type: Text
  content: $t.admin.invitations.send_form_hint
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-variant

# Three lines, in tab order: the email; the two name parts; the role beside Send.
# The row wraps and the name row claims a whole line (`flex: 1 1 100%`), so the email
# and the role each get the full width a long value needs. Lines align `flex-start`:
# a field keeps its place when an error or a helper appears under it or under its
# neighbour, and only the Send button, which carries no label, sits at the line's end.
invitation-send-fields:
  type: Section
  spatial:
    display: flex
    wrap: wrap
    gap: $spacing.sm
    align: flex-start
    responsive:
      sm:
        direction: column
        align: stretch
  children:
    - invitation-email-input
    - invitation-name-fields
    - invitation-role-input
    - invitation-send-btn

invitation-name-fields:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
    align: flex-start
    flex: 1 1 100%
    responsive:
      sm:
        direction: column
        align: stretch
  children:
    - invitation-first-name-input
    - invitation-last-name-input

invitation-email-input:
  type: Input
  input_type: email
  label: $t.admin.invitations.field_email
  bind: form.email
  a11y:
    label: $t.admin.invitations.field_email_label
  validation:
    required: true
  spatial:
    flex: 1
    min_height: $size.touch-target

# The invited person's name: the directory user the invitation creates carries a
# first and a last name, 1..255 characters each, and the server refuses a send
# without them.
invitation-first-name-input:
  type: Input
  input_type: text
  # `model:` lets the emitter resolve the bound field of an Input that sits outside a
  # Form, which is what puts the field's `maxlength` on the element.
  model: CreateInvitationForm
  label: $t.admin.invitations.field_first_name
  helper: $t.admin.invitations.name_hint
  bind: form.firstName
  a11y:
    label: $t.admin.invitations.field_first_name_label
  # Mirrors PersonName (Xp87.Domain): at least one letter, and none of the characters
  # Keycloak's person-name rule prohibits — < > & " $ % ! # ? § ; * ~ / \ | ^ = [ ] { } ( ) —
  # nor a control character, a line or paragraph separator, or a text-direction control
  # (U+061C, U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069; the two hyphens in the
  # class are ranges). The zero-width joiner and non-joiner stay allowed. Every punctuation
  # mark is escaped, and the four HTML-significant ones are written as \xNN: the browser
  # compiles `pattern` with the `v` flag and ignores one that fails there (XP85043). The
  # 255-character bound is the bound model field's `max_length`, which emits `maxlength`.
  validation:
    required: true
    pattern: '^(?=.*\p{L})[^\x22\x26\x3C\x3E\$\%\!\#\?§\;\*\~\/\\\|\^\=\[\]\{\}\(\)\p{Cc}\u2028\u2029\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]+$'
    messages:
      required: $t.admin.invitations.first_name_required
      pattern: $t.admin.invitations.name_invalid
  spatial:
    flex: 1
    min_height: $size.touch-target

invitation-last-name-input:
  type: Input
  input_type: text
  # `model:` lets the emitter resolve the bound field of an Input that sits outside a
  # Form, which is what puts the field's `maxlength` on the element.
  model: CreateInvitationForm
  label: $t.admin.invitations.field_last_name
  bind: form.lastName
  a11y:
    label: $t.admin.invitations.field_last_name_label
  # Mirrors PersonName (Xp87.Domain): at least one letter, and none of the characters
  # Keycloak's person-name rule prohibits — < > & " $ % ! # ? § ; * ~ / \ | ^ = [ ] { } ( ) —
  # nor a control character, a line or paragraph separator, or a text-direction control
  # (U+061C, U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069; the two hyphens in the
  # class are ranges). The zero-width joiner and non-joiner stay allowed. Every punctuation
  # mark is escaped, and the four HTML-significant ones are written as \xNN: the browser
  # compiles `pattern` with the `v` flag and ignores one that fails there (XP85043). The
  # 255-character bound is the bound model field's `max_length`, which emits `maxlength`.
  validation:
    required: true
    pattern: '^(?=.*\p{L})[^\x22\x26\x3C\x3E\$\%\!\#\?§\;\*\~\/\\\|\^\=\[\]\{\}\(\)\p{Cc}\u2028\u2029\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]+$'
    messages:
      required: $t.admin.invitations.last_name_required
      pattern: $t.admin.invitations.name_invalid
  spatial:
    flex: 1
    min_height: $size.touch-target

# Required role picker. Dropdown of the roles that can be granted
# (GET /api/users/roles?assignable=true).
invitation-role-input:
  type: Input
  input_type: select
  label: $t.admin.invitations.role
  placeholder: $t.admin.invitations.role_placeholder
  bind:
    value: form.intendedRole
    options: roles
  a11y:
    label: $t.admin.invitations.role_label
  validation:
    required: true
  spatial:
    flex: 1
    min_height: $size.touch-target

# Send button — POST /api/invitations with the local `form` model.
#
# Toast contract (F71 §B.3):
#   - The direct fetch dispatches `xp85-data-changed` after a 2xx response, so
#     the invitation table refetches and the newly-sent row appears in place.
#   - The emitted direct fetch dispatches `xp85-action-complete`, the same
#     completion event the form dispatcher raises, and the router's handler
#     for it is the one renderer of action toasts on every action path: the
#     success toast on a 2xx, an error toast (10 s) carrying the response's
#     error detail on a 4xx/5xx, and one warning toast per entry of the
#     envelope's `warnings[]` (e.g. the invitation email could not be sent),
#     which stays until the operator dismisses it.
#     No per-CIR toast wiring is required for any of those branches.
#   - `body: form` makes the action validate the form's fields before it
#     fetches: an invalid field is marked and focused, and nothing is sent.
invitation-send-btn:
  type: Button
  variant: filled
  color: info
  content: $t.admin.invitations.send
  a11y:
    label: $t.admin.invitations.send_label
  action:
    type: custom
    method: POST
    url: /api/invitations
    body: form
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
    # Level with the role select beside it, whose label sits above its control.
    align_self: flex-end
    responsive:
      sm:
        align_self: stretch
  tokens:
    radius: $radius.md

# ── Invitation table ─────────────────────────────────────────────────────────

invitation-list-table:
  type: Table
  bind:
    rows: invitations
  a11y:
    label: $t.admin.invitations.table_label
  # M1 list-filter adoption (DEC-090) — opt-in client-side quick-filter over the
  # loaded rows: case-insensitive substring across email / status + native
  # clear. Complements the server-side status filter (WithRequiresFilter).
  filter:
    fields: [email, status]
    placeholder: $t.common.quick_filter
  columns:
    - key: email
      label: $t.admin.invitations.col_email
      width: 2fr
    - key: status
      label: $t.admin.invitations.col_status
      width: 1fr
    - key: createdAt
      label: $t.admin.invitations.col_sent
      format: date-relative
      width: 1fr
    - key: expiresAt
      label: $t.admin.invitations.col_expires
      format: date
      width: 1fr
  empty: invitation-list-empty

invitation-list-empty:
  type: EmptyState
  content: $t.admin.invitations.empty
  body: $t.admin.invitations.empty_body
